import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DebouncedAutocomplete from 'Components/DebouncedAutocomplete';
import TextField from '@material-ui/core/TextField';
import { Button, Chip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  buildCustomerOptions,
  buildProjectOptions,
  certificateSelectionValues,
  filterProjectOptions,
  normalizeCertificateOptions,
  selectionIds,
} from '../../Pages/Reporting/learningReportFields';

const useStyles = makeStyles((theme) => ({
  filterPanel: {
    width: '100%',
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2, 2.25),
    border: '1px solid #e7ebf0',
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5),
    },
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.75),
  },
  filterHeaderText: {
    minWidth: 0,
    flex: 1,
  },
  filterHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
  filterEyebrow: {
    color: theme.palette.primary.main,
    fontSize: '0.6875rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: theme.spacing(0.25),
  },
  filterTitle: {
    color: '#263548',
    fontSize: '0.875rem',
    fontWeight: 700,
    lineHeight: 1.35,
  },
  filterGrid: {
    alignItems: 'flex-start',
  },
  autocomplete: {
    width: '100%',
    '& .MuiAutocomplete-inputRoot': {
      backgroundColor: '#fff',
      borderRadius: 8,
      minHeight: 40,
      paddingTop: '4px !important',
      paddingBottom: '4px !important',
      transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
      '& fieldset': {
        borderColor: '#e7ebf0',
        borderWidth: '1px !important',
      },
      '&:hover fieldset': {
        borderColor: '#d5dde8',
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: '0 0 0 3px rgba(61, 125, 221, 0.12)',
        '& fieldset': {
          borderColor: `${theme.palette.primary.main} !important`,
          borderWidth: '1px !important',
        },
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: '#263548',
      padding: '6px 4px !important',
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '0.8125rem',
      color: '#647184',
      fontWeight: 500,
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      color: '#647184',
      fontWeight: 600,
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiAutocomplete-endAdornment': {
      right: 6,
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#94a3b8',
      padding: 4,
      '&:hover': {
        backgroundColor: 'rgba(61, 125, 221, 0.08)',
        color: theme.palette.primary.main,
      },
    },
  },
  chip: {
    height: 24,
    maxWidth: '100%',
    borderRadius: 6,
    border: '1px solid #e7ebf0',
    backgroundColor: '#f5f8fc',
    color: '#263548',
    fontSize: '0.75rem',
    fontWeight: 600,
    '& .MuiChip-label': {
      paddingLeft: 8,
      paddingRight: 8,
    },
    '& .MuiChip-deleteIcon': {
      width: 16,
      height: 16,
      color: '#94a3b8',
      marginRight: 4,
      '&:hover': {
        color: '#647184',
      },
    },
  },
  paper: {
    marginTop: 4,
    borderRadius: 8,
    border: '1px solid #e7ebf0',
    boxShadow: '0 8px 24px rgba(12, 41, 84, 0.1)',
    overflow: 'hidden',
  },
  listbox: {
    padding: theme.spacing(0.75),
    maxHeight: 280,
    '& .MuiAutocomplete-option': {
      minHeight: 36,
      borderRadius: 6,
      margin: theme.spacing(0.25, 0),
      padding: theme.spacing(0.875, 1.25),
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: '#263548',
      lineHeight: 1.35,
      '&[data-focus="true"]': {
        backgroundColor: 'rgba(61, 125, 221, 0.08)',
      },
      '&[aria-selected="true"]': {
        backgroundColor: 'rgba(61, 125, 221, 0.12)',
      },
    },
  },
  noOptions: {
    fontSize: '0.8125rem',
    color: '#647184',
    padding: theme.spacing(1.25, 1.5),
  },
  filterBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: '0.8125rem',
    fontWeight: 600,
    height: 40,
    minHeight: 40,
    minWidth: 120,
    textTransform: 'none',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  filterBtnCell: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  filterBtnCellLearning: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
  },
  filterBtnRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    width: '100%',
  },
  clearBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: '0.8125rem',
    fontWeight: 600,
    height: 40,
    minHeight: 40,
    minWidth: 120,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    borderColor: '#d5dde8',
    color: '#4D5766',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: '#c5ced9',
      backgroundColor: '#f8fafc',
    },
    '&.Mui-disabled': {
      borderColor: '#e7ebf0',
      color: '#94a3b8',
    },
  },
  dateField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: 8,
      minHeight: 40,
      '& fieldset': {
        borderColor: '#e7ebf0',
      },
      '&:hover fieldset': {
        borderColor: '#d5dde8',
      },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(61, 125, 221, 0.12)',
        '& fieldset': {
          borderColor: `${theme.palette.primary.main} !important`,
        },
      },
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '0.8125rem',
      color: '#647184',
      fontWeight: 500,
    },
    '& .MuiInputBase-input': {
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: '#263548',
      padding: '10.5px 14px',
    },
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  appliedPeriod: {
    fontSize: '0.8125rem',
    color: '#647184',
    fontWeight: 500,
  },
  filterError: {
    fontSize: '0.8125rem',
    color: '#b42318',
    fontWeight: 500,
  },
  dateError: {
    fontSize: '0.75rem',
    color: '#b42318',
    marginTop: 4,
  },
}));

export const PERIOD_OPTIONS = [
  { label: 'All time', value: 'all_time' },
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'Last 3 months', value: 'last_3_months' },
  { label: 'Last 6 months', value: 'last_6_months' },
  { label: 'Last 12 months', value: 'last_12_months' },
  { label: 'Custom range', value: 'custom' },
];

const getOptionText = (option) => {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') return String(option);
  return option.name || option.label || option.email || '';
};

const optionsMatch = (option, value) => {
  if (option === value) return true;
  if (option == null || value == null) return false;
  if (typeof option === 'object' && typeof value === 'object') {
    if (option.id != null && value.id != null && String(option.id) === String(value.id)) return true;
    if (
      Object.prototype.hasOwnProperty.call(option, 'value')
      && Object.prototype.hasOwnProperty.call(value, 'value')
      && option.id == null
      && value.id == null
    ) {
      return Object.is(option.value, value.value);
    }
    if (option.value != null && value.value != null) return option.value === value.value;
  }
  return getOptionText(option) === getOptionText(value);
};

const AutoCompleteFilter = ({
  multiple,
  option = [],
  handleChange,
  label,
  value,
  onInputChange,
  id,
  disableClearable = false,
}) => {
  const classes = useStyles();
  const safeOptions = Array.isArray(option) ? option.filter((opt) => opt != null && opt !== '') : [];
  const SearchInput = label === 'Email' ? DebouncedAutocomplete : Autocomplete;
  const safeValue = multiple
    ? (Array.isArray(value) ? value : [])
    : (value == null ? null : value);

  return (
    <SearchInput
      id={id}
      className={classes.autocomplete}
      multiple={multiple}
      value={safeValue}
      options={safeOptions}
      limitTags={1}
      disableClearable={disableClearable}
      disableCloseOnSelect={multiple}
      getOptionSelected={optionsMatch}
      getOptionLabel={getOptionText}
      onChange={handleChange}
      classes={{
        paper: classes.paper,
        listbox: classes.listbox,
        noOptions: classes.noOptions,
      }}
      noOptionsText="No options"
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((opt, index) => {
          const tagProps = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={opt?.key || (opt?.id != null ? String(opt.id) : tagProps.key)}
              label={getOptionText(opt)}
              size="small"
              className={classes.chip}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          fullWidth
          onChange={(ev) => {
            if (onInputChange) {
              onInputChange(ev.target.value);
            }
          }}
        />
      )}
    />
  );
};

const CustomAutocomplete = ({ option = [], handleChange, label, value, onInputChange }) => {
  const classes = useStyles();
  const safeOptions = Array.isArray(option) ? option.filter((opt) => opt != null && opt !== '') : [];
  const safeValue = Array.isArray(value) ? value : [];

  return (
    <Autocomplete
      className={classes.autocomplete}
      multiple
      value={safeValue}
      options={safeOptions}
      limitTags={1}
      disableCloseOnSelect
      getOptionSelected={optionsMatch}
      getOptionLabel={getOptionText}
      onChange={handleChange}
      classes={{
        paper: classes.paper,
        listbox: classes.listbox,
        noOptions: classes.noOptions,
      }}
      noOptionsText="No options"
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((opt, index) => (
          <Chip
            key={getOptionText(opt) || index}
            label={getOptionText(opt)}
            size="small"
            className={classes.chip}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || null}
          variant="outlined"
          size="small"
          fullWidth
          onChange={(ev) => {
            if (onInputChange) {
              onInputChange(ev.target.value);
            }
          }}
        />
      )}
    />
  );
};

const FilterPanelHeader = ({ classes, headerActions }) => (
  <div className={classes.filterHeader}>
    <div className={classes.filterHeaderText}>
      <Typography className={classes.filterEyebrow}>Filters</Typography>
      <Typography className={classes.filterTitle}>Refine report results</Typography>
    </div>
    {headerActions ? (
      <div className={classes.filterHeaderActions} id="table_h_icon">
        {headerActions}
      </div>
    ) : null}
  </div>
);

const getCustomRangeErrors = (allFilterState) => {
  if ((allFilterState?.date_range || 'all_time') !== 'custom') {
    return { from: '', to: '', invalid: false };
  }
  const from = allFilterState?.date_from || '';
  const to = allFilterState?.date_to || '';
  const fromError = !from ? 'From date is required' : '';
  const toError = !to
    ? 'To date is required'
    : (from && to && from > to ? 'To date must be on or after From date' : '');
  const fromOrderError = from && to && from > to && !fromError
    ? 'From date must be on or before To date'
    : fromError;
  return {
    from: fromOrderError,
    to: toError,
    invalid: Boolean(fromOrderError || toError),
  };
};

const hasLearningFilters = (state) => {
  if (!state) return false;
  return Boolean(
    (state.customer && state.customer.length)
    || (state.certificate_status && state.certificate_status.length)
    || (state.project && state.project.length)
    || (state.course && state.course.length)
    || state.email
    || ((state.date_range || 'all_time') !== 'all_time')
    || state.date_from
    || state.date_to
  );
};

const hasReadlineFilters = (state) => {
  if (!state) return false;
  return Boolean(
    (state.customer && state.customer.length)
    || (state.project && state.project.length)
    || (state.users && state.users.length)
  );
};

const CustamizedFilters = ({
  filters,
  handleChange,
  allFilterState,
  type,
  handleSubmitFilter,
  handleClearFilters,
  pageName,
  headerActions,
  applyDisabled = false,
  appliedPeriodText = '',
  filterError = '',
  loading = false,
}) => {
  const classes = useStyles();
  const [customApplyAttempted, setCustomApplyAttempted] = React.useState(false);
  const periodValue = PERIOD_OPTIONS.find(
    (opt) => opt.value === (allFilterState?.date_range || 'all_time')
  ) || PERIOD_OPTIONS[0];
  const isCustomRange = periodValue.value === 'custom';
  const customErrors = getCustomRangeErrors(allFilterState);
  // Only surface custom-range errors after the user clicks Apply filters.
  const visibleCustomErrors = customApplyAttempted && isCustomRange
    ? customErrors
    : { from: '', to: '', invalid: false };
  const disableApply = applyDisabled || loading;
  const canClear = pageName === 'learning'
    ? hasLearningFilters(allFilterState)
    : pageName === 'readline'
      ? hasReadlineFilters(allFilterState)
      : false;

  React.useEffect(() => {
    if (!isCustomRange) {
      setCustomApplyAttempted(false);
    }
  }, [isCustomRange]);

  const handleFilter = () => {
    if (disableApply) return;
    if (isCustomRange && customErrors.invalid) {
      setCustomApplyAttempted(true);
      return;
    }
    setCustomApplyAttempted(false);
    if (typeof handleSubmitFilter === 'function') {
      handleSubmitFilter(type);
    }
  };

  const handleClear = () => {
    if (!canClear || loading || typeof handleClearFilters !== 'function') return;
    setCustomApplyAttempted(false);
    handleClearFilters(type);
  };

  const customerOptions = buildCustomerOptions(filters);
  const selectedCustomerIds = Array.isArray(allFilterState?.customer) ? allFilterState.customer : [];
  const allProjectOptions = buildProjectOptions(filters);
  const projectOptions = filterProjectOptions(allProjectOptions, selectedCustomerIds);
  const certificateOptions = normalizeCertificateOptions(filters?.certificate_status);
  const selectedCustomers = selectedCustomerIds.map((id) => (
    customerOptions.find((opt) => String(opt.id) === String(id)) || { id, name: String(id) }
  ));
  const selectedProjects = (Array.isArray(allFilterState?.project) ? allFilterState.project : [])
    .map((id) => projectOptions.find((opt) => String(opt.id) === String(id)))
    .filter(Boolean);
  const selectedCertificates = (Array.isArray(allFilterState?.certificate_status) ? allFilterState.certificate_status : [])
    .map((value) => (
      certificateOptions.find((opt) => Object.is(opt.value, value))
      || { label: value == null ? 'Unknown' : String(value), value, key: `selected-${String(value)}` }
    ));

  return (
    <>
      {pageName === 'learning' && (
        <div className={classes.filterPanel}>
          <FilterPanelHeader classes={classes} headerActions={headerActions} />
          <Grid container spacing={2} className={classes.filterGrid}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <AutoCompleteFilter
                id={`learning-filter-customer-${type}`}
                multiple
                value={selectedCustomers}
                label="Customer"
                handleChange={(evt, newVal) => handleChange(selectionIds(newVal), 'customer', type)}
                option={customerOptions}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <AutoCompleteFilter
                id={`learning-filter-certificate-${type}`}
                multiple
                value={selectedCertificates}
                label="Certificate"
                handleChange={(evt, newVal) => handleChange(certificateSelectionValues(newVal), 'certificate_status', type)}
                option={certificateOptions}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <AutoCompleteFilter
                id={`learning-filter-project-${type}`}
                multiple
                value={selectedProjects}
                label="Project"
                handleChange={(evt, newVal) => handleChange(selectionIds(newVal), 'project', type)}
                option={projectOptions}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <AutoCompleteFilter
                id={`learning-filter-course-${type}`}
                multiple
                value={allFilterState ? allFilterState.course : []}
                label="Course"
                handleChange={(evt, newVal) => handleChange(newVal, 'course', type)}
                option={filters ? filters.course : []}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <AutoCompleteFilter
                id={`learning-filter-email-${type}`}
                multiple={false}
                value={allFilterState?.email || null}
                label="Email"
                handleChange={(evt, newVal) => handleChange(newVal, 'email', type)}
                option={filters ? filters.email : []}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <AutoCompleteFilter
                id={`learning-filter-period-${type}`}
                multiple={false}
                value={periodValue}
                label="Period"
                handleChange={(evt, newVal) => handleChange(newVal?.value || 'all_time', 'date_range', type)}
                option={PERIOD_OPTIONS}
              />
            </Grid>
            {isCustomRange && (
              <>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    className={classes.dateField}
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="date"
                    label="From"
                    name="date_from"
                    value={allFilterState?.date_from || ''}
                    onChange={(evt) => handleChange(evt.target.value, 'date_from', type)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: allFilterState?.date_to || undefined }}
                    error={Boolean(visibleCustomErrors.from)}
                    helperText={visibleCustomErrors.from || ' '}
                    FormHelperTextProps={{ className: visibleCustomErrors.from ? classes.dateError : undefined }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    className={classes.dateField}
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="date"
                    label="To"
                    name="date_to"
                    value={allFilterState?.date_to || ''}
                    onChange={(evt) => handleChange(evt.target.value, 'date_to', type)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: allFilterState?.date_from || undefined }}
                    error={Boolean(visibleCustomErrors.to)}
                    helperText={visibleCustomErrors.to || ' '}
                    FormHelperTextProps={{ className: visibleCustomErrors.to ? classes.dateError : undefined }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} className={classes.filterBtnCellLearning}>
              <div className={classes.filterBtnRow}>
                <Button
                  className={classes.clearBtn}
                  onClick={handleClear}
                  variant="outlined"
                  disableElevation
                  disabled={!canClear || loading}
                >
                  Clear filters
                </Button>
                <Button
                  className={classes.filterBtn}
                  onClick={handleFilter}
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={disableApply}
                >
                  Apply filters
                </Button>
              </div>
            </Grid>
          </Grid>
          {(appliedPeriodText || filterError) && (
            <div className={classes.metaRow}>
              {appliedPeriodText ? (
                <Typography className={classes.appliedPeriod}>
                  Period: {appliedPeriodText}
                </Typography>
              ) : null}
              {filterError ? (
                <Typography className={classes.filterError}>{filterError}</Typography>
              ) : null}
            </div>
          )}
        </div>
      )}
      {pageName === 'readline' && (
        <div className={classes.filterPanel}>
          <FilterPanelHeader classes={classes} headerActions={headerActions} />
          <Grid container spacing={2} className={classes.filterGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <AutoCompleteFilter
                multiple
                value={allFilterState ? allFilterState.customer : []}
                label="Customer"
                handleChange={(evt, newVal) => handleChange(newVal, 'customer', type)}
                option={filters ? filters.customer : []}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AutoCompleteFilter
                multiple
                value={allFilterState ? allFilterState.project : []}
                label="Project"
                handleChange={(evt, newVal) => handleChange(newVal, 'project', type)}
                option={filters ? filters.project : []}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AutoCompleteFilter
                multiple
                value={allFilterState ? allFilterState.users : []}
                label="Users"
                handleChange={(evt, newVal) => handleChange(newVal, 'users', type)}
                option={filters ? filters.users : []}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.filterBtnCell}>
              <div className={classes.filterBtnRow}>
                <Button
                  className={classes.clearBtn}
                  onClick={handleClear}
                  variant="outlined"
                  disableElevation
                  disabled={!canClear || loading}
                >
                  Clear filters
                </Button>
                <Button
                  className={classes.filterBtn}
                  onClick={handleFilter}
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={disableApply}
                >
                  Apply filters
                </Button>
              </div>
            </Grid>
          </Grid>
        </div>
      )}
      {pageName === 'userFilter' && (
        <div className={classes.filterPanel}>
          <FilterPanelHeader classes={classes} headerActions={headerActions} />
          <Grid container spacing={2} className={classes.filterGrid}>
            <Grid item xs={12} sm={8} md={6} lg={4}>
              <CustomAutocomplete
                value={allFilterState ? allFilterState.users : []}
                label="Users"
                handleChange={(evt, newVal) => handleChange(newVal, 'user', type)}
                option={filters ? filters : []}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} className={classes.filterBtnCell}>
              <Button
                className={classes.filterBtn}
                onClick={handleFilter}
                variant="contained"
                color="primary"
                disableElevation
              >
                Apply filters
              </Button>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};

export default CustamizedFilters;
